import { Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, Observable, take, tap } from 'rxjs';

import { AnalyticEvent } from '@app/core/analytics.service';
import { AppointmentAnalyticsProperty } from '@app/core/appointment-analytics-base.service';
import { LinksService } from '@app/core/links.service';
import { FLOW_APPOINTMENT_BOOKING } from '@app/core/mixpanel.constants';
import { InsuranceCaptureService } from '@app/shared/insurance-capture/insurance-capture.service';
import { PaymentCaptureService, PaymentMethodData } from '@app/shared/payment-capture/payment-capture.service';

import { AppointmentAnalyticsService } from '../appointment-analytics.service';
import { AppointmentBookingStateService } from '../appointment-booking-state-service';

export enum PaymentStep {
  CONFIRM_METHOD = 'confirm-method',
  COPAY_REQUIRED = 'copay-required',
  SELECT_METHOD = 'select-method',
  ADD_METHOD = 'add-method',
}

export enum ManagePaymentMethodsQueryParams {
  SOURCE = 'source',
  CONFIRMATION_ID = 'confirmationId',
  IS_NEW_INSURANCE = 'new',
}

export enum ManagePaymentMethodsSource {
  FALLBACK = 'fallback',
  INSURANCE = 'insurance',
  REVIEW = 'review',
  CONFIRMATION = 'confirmation',
}

export const ManagePaymentMethodsSourcePropertyMap: Record<ManagePaymentMethodsSource, string | undefined> = {
  [ManagePaymentMethodsSource.FALLBACK]: undefined,
  [ManagePaymentMethodsSource.INSURANCE]: AppointmentAnalyticsProperty.AddInsurancePagePreBookingModalModule,
  [ManagePaymentMethodsSource.REVIEW]: AppointmentAnalyticsProperty.AppointmentSelectedPage,
  [ManagePaymentMethodsSource.CONFIRMATION]: AppointmentAnalyticsProperty.AppointmentConfirmationPageModule,
} as const;

@Component({
  selector: 'om-manage-payment-methods',
  templateUrl: './manage-payment-methods.component.html',
  styleUrls: ['./manage-payment-methods.component.scss'],
})
export class ManagePaymentMethodsComponent implements OnInit {
  readonly PaymentStep = PaymentStep;
  currentStep: PaymentStep;
  showSkip: boolean;
  showBack: boolean;
  source: ManagePaymentMethodsSource;
  confirmationId: string;
  sourceProp: string;
  isNewInsurance: boolean;
  historyStack: PaymentStep[] = [];
  defaultCopayMethod: PaymentMethodData | null;
  paymentMethods: PaymentMethodData[];
  currentPaymentMethod: PaymentMethodData;
  canSetDefault: boolean;
  copay: number;
  skipNewInsurance: boolean;
  skipExistingInsurance: boolean;
  protected loading = true;

  constructor(
    private readonly paymentCaptureService: PaymentCaptureService,
    private readonly insuranceCaptureService: InsuranceCaptureService,
    private readonly router: Router,
    private readonly links: LinksService,
    private readonly route: ActivatedRoute,
    private readonly location: Location,
    private readonly analyticsService: AppointmentAnalyticsService,
    private readonly appointmentBookingStateService: AppointmentBookingStateService,
  ) {}

  ngOnInit() {
    const params = this.route.snapshot.queryParamMap;
    this.source = params.get(ManagePaymentMethodsQueryParams.SOURCE) as ManagePaymentMethodsSource;
    this.confirmationId = params.get(ManagePaymentMethodsQueryParams.CONFIRMATION_ID);
    this.isNewInsurance = params.get(ManagePaymentMethodsQueryParams.IS_NEW_INSURANCE) === 'true';

    // If source unknown, set to FALLBACK
    if (!Object.values(ManagePaymentMethodsSource).includes(this.source)) {
      this.source = ManagePaymentMethodsSource.FALLBACK;
    }

    this.sourceProp = ManagePaymentMethodsSourcePropertyMap[this.source];

    combineLatest([
      this.insuranceCaptureService.getPrimaryInsurance$(),
      this.paymentCaptureService.getPaymentMethods$(),
      this.paymentCaptureService.getSkipPaymentCaptureNewInsuranceFlagEnabled$(),
      this.paymentCaptureService.getSkipPaymentCaptureExistingInsuranceFlagEnabled$(),
    ])
      .pipe(take(1))
      .subscribe(([insurance, paymentMethods, skipNewInsurance, skipExistingInsurance]) => {
        this.copay = insurance.copay || 0;
        this.canSetDefault = this.source === ManagePaymentMethodsSource.INSURANCE && this.copay > 0;
        this.mapPaymentMethods(paymentMethods);
        this.skipNewInsurance = skipNewInsurance;
        this.skipExistingInsurance = skipExistingInsurance;
        this.loading = false;
        if (!this.currentStep) {
          const initialStep = this.getInitialStep();
          this.goToStep(initialStep);
        }
      });
  }

  goBack() {
    if (this.historyStack.length > 1) {
      // If there's local history, go back within component
      this.historyStack.pop();
      const step = this.historyStack.pop() as PaymentStep;
      this.goToStep(step);
    } else {
      this.location.back();
    }
  }

  goToStep(step: PaymentStep) {
    this.historyStack.push(step);

    // Hide back button on first step if unknown source
    const isFirstStep = step === this.historyStack[0];
    this.showBack = !(this.source === ManagePaymentMethodsSource.FALLBACK && isFirstStep);

    // Show skip ("I'll add it later" button) if:
    // 1. Source is insurance
    // 2. New Insurance + SkipNewInsurance Flag || Existing Insurance + SkipExistingInsuranceFlag
    // 3. It's the first step (ignoring Confirm Method page when card is valid)
    this.showSkip =
      this.source === ManagePaymentMethodsSource.INSURANCE &&
      ((this.isNewInsurance && this.skipNewInsurance) || (!this.isNewInsurance && this.skipExistingInsurance)) &&
      step ===
        this.historyStack.find(s => !(s === PaymentStep.CONFIRM_METHOD && !this.currentPaymentMethod?.isExpired));

    this.currentStep = step;
  }

  goToDestination() {
    if (this.confirmationId) {
      this.router.navigate([this.links.appointmentConfirmation(this.confirmationId)]);
    } else if (this.appointmentBookingStateService.getAppointmentBookingState()?.appointmentType) {
      this.router.navigate([this.links.appointmentReview]);
    } else {
      this.router.navigate([this.links.home]);
    }
  }

  refreshPaymentMethods$(): Observable<PaymentMethodData[]> {
    return this.paymentCaptureService.getPaymentMethods$().pipe(
      take(1),
      tap(paymentMethods => this.mapPaymentMethods(paymentMethods)),
    );
  }

  trackAnalytics(analytics: AnalyticEvent) {
    this.analyticsService.trackEventWithDefaultProperties({
      flow: FLOW_APPOINTMENT_BOOKING,
      source: ManagePaymentMethodsSourcePropertyMap[this.source],
      ...analytics,
    });
  }

  private mapPaymentMethods(paymentMethods: PaymentMethodData[]) {
    this.paymentMethods = paymentMethods;
    this.defaultCopayMethod = this.paymentMethods.find(method => method.isDefaultCopay);
    this.currentPaymentMethod = this.defaultCopayMethod || paymentMethods?.[0];
  }

  private getInitialStep() {
    if (this.canSetDefault && this.isNewInsurance) {
      return PaymentStep.COPAY_REQUIRED;
    } else if (this.paymentMethods.length) {
      return PaymentStep.SELECT_METHOD;
    } else {
      return PaymentStep.ADD_METHOD;
    }
  }
}
